import React, { useState } from "react";
import { Seo } from "@/components/Seo";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { VOLUNTEER } from "@/constants/testIds";
import { toast } from "sonner";
import { ReferralWelcomeBanner } from "@/components/ReferralWelcomeBanner";
import { TypeStep } from "@/components/onboarding/TypeStep";
import { ProfileBasicsSection } from "@/components/onboarding/ProfileBasicsSection";
import { PrivacySection } from "@/components/onboarding/PrivacySection";
import { ValuesSection, SkillsSection } from "@/components/onboarding/TagsSections";
import { RoleBadgeSection } from "@/components/onboarding/RoleBadgeSection";
import { CalendarSubscribeContent } from "@/components/CalendarSubscribe";
import { CalendarPlus, Sparkles } from "lucide-react";

// Onboarding/settings page: state + handlers live here; presentational
// sections are in components/onboarding/* (Phase 15 component split —
// behaviour unchanged).
export default function Onboarding() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(user?.user_type ? 2 : 1);
  const [type, setType] = useState(user?.user_type || "individual");
  const [orgName, setOrgName] = useState(user?.org_name || "");
  const [displayName, setDisplayName] = useState(user?.display_name || user?.name || "");
  const [realName, setRealName] = useState(user?.real_name || "");
  const [showRealName, setShowRealName] = useState(!!user?.show_real_name);
  const [bio, setBio] = useState(user?.bio || "");
  const [location, setLocation] = useState(user?.location || user?.invite_prefill?.area || "");
  const [lat, setLat] = useState(user?.lat ?? null);
  const [lng, setLng] = useState(user?.lng ?? null);
  const [values, setValues] = useState(new Set(user?.values?.length ? user.values : (user?.invite_prefill?.interests || [])));
  const [showOnLeaderboard, setShowOnLeaderboard] = useState(user?.show_on_leaderboard !== false);
  const [profilePrivate, setProfilePrivate] = useState(!!user?.profile_private);
  const [emailOptOut, setEmailOptOut] = useState(!!user?.email_opt_out);
  const [skills, setSkills] = useState(new Set(user?.skills || []));
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]); // list of {skill, confidence}
  // Phase 17-B (2026-07): first-run completion screen — carries the intent
  // destination on its primary button and links /explore + /pathways.
  const [done, setDone] = useState(null); // { to } | null

  const suggestSkills = async () => {
    if (!bio.trim()) {
      toast.error("Write a short bio first — that's what the AI reads.");
      return;
    }
    setSuggesting(true);
    try {
      const r = await api.extractSkills(bio.trim(), null);
      const items = [
        ...(r.suggested_skills || []).map((s) => ({
          skill: s,
          confidence: r.confidence?.[s] ?? null,
          custom: false,
        })),
        ...(r.custom_skills || []).map((s) => ({ skill: s, confidence: null, custom: true })),
      ];
      setSuggestions(items);
      if (items.length === 0) {
        toast("No matching skills detected — try adding more detail to your bio.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't suggest skills right now — try again.");
    } finally {
      setSuggesting(false);
    }
  };

  const addSuggestedSkill = (s) => {
    setSkills((prev) => {
      const next = new Set(prev);
      next.add(s);
      return next;
    });
  };

  const toggle = (set, setter, val) => {
    const next = new Set(set);
    next.has(val) ? next.delete(val) : next.add(val);
    setter(next);
  };

  const submit = async () => {
    // B1: first-run onboarding carries the landing-hero intent (help /
    // need_help / community) — stored on the user, then used to route the
    // very first screen. Returning users (settings edits) are unaffected.
    const firstRun = !user?.onboarded;
    let intent = null;
    try { intent = sessionStorage.getItem("civic_intent"); } catch { /* ignore */ }
    // 17-C item 6: arriving via a collective "Join" round-trip? Collectives
    // are area-gated server-side, so require the location NOW rather than
    // letting the auto-join fail right after onboarding.
    let joinFlag = null;
    try { joinFlag = sessionStorage.getItem("civic_return_join"); } catch { /* ignore */ }
    if (joinFlag && type !== "organisation" && (lat == null || lng == null)) {
      toast.error("Add your area first — the collective you're joining checks that you're a local.");
      return;
    }
    try {
      const u = await api.updateProfile({
        user_type: type,
        org_name: type === "organisation" ? orgName : null,
        bio, location, lat, lng,
        values: [...values],
        skills: [...skills],
        show_on_leaderboard: showOnLeaderboard,
        profile_private: profilePrivate,
        email_opt_out: emailOptOut,
        ...(firstRun && intent ? { intent } : {}),
        ...(type !== "organisation" && {
          display_name: displayName.trim(),
          real_name: realName.trim(),
          show_real_name: showRealName,
        }),
      });
      setUser(u);
      toast.success("Profile saved");
      try { sessionStorage.removeItem("civic_intent"); } catch { /* ignore */ }
      // B1 + 17-B item 4: first-run users see a completion screen whose
      // primary button carries the intent destination (individuals) or the
      // dashboard (orgs, who go post their first opportunity). The screen
      // also links /explore ("Take the tour") and /pathways. Returning users
      // editing settings go straight back to the dashboard as before.
      if (firstRun) {
        let to = "/dashboard";
        if (type !== "organisation") {
          if (intent === "need_help") to = "/help-request/new";
          else if (intent === "community") to = "/collectives";
          else if (intent === "help") to = "/opportunities";
        }
        // 17-C item 6: a logged-out "Join" that round-tripped through signup
        // routes back to the collective — CollectiveDetail consumes the flag
        // and completes the join automatically.
        let joinId = null;
        try { joinId = sessionStorage.getItem("civic_return_join"); } catch { /* ignore */ }
        if (joinId) to = `/collectives/${joinId}`;
        return setDone({ to });
      }
      nav("/dashboard");
    } catch (e) {
      toast.error("Could not save profile");
    }
  };

  return (
    <div className="min-h-screen bg-cream py-16 px-6">
      <Seo title="Onboarding" />
      <div className="max-w-3xl mx-auto">
        <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">
          {done ? "Onboarding · Complete" : `Onboarding · Step ${step} of 2`}
        </div>
        <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">
          {done ? "You're all set" : "Let's set you up"}
        </h1>
        <div className="mt-6"><ReferralWelcomeBanner /></div>

        {/* 17-D carry-over: arrived via a collective Join — friendly steer
            instead of a raw "add a location" error later. */}
        {!done && (() => {
          let joinFlag = null;
          try { joinFlag = sessionStorage.getItem("civic_return_join"); } catch { /* ignore */ }
          return joinFlag ? (
            <div data-testid="onboarding-join-note" className="mt-6 bg-forest/5 border border-forest/15 rounded-xl px-5 py-4 text-sm text-forest flex items-start gap-2.5">
              <Sparkles className="w-4 h-4 text-terracotta shrink-0 mt-0.5" />
              <span>
                Almost there — tell us where you are so we can connect you locally.
                We'll take you straight back to the collective you're joining.
              </span>
            </div>
          ) : null;
        })()}

        {done && (
          <Card data-testid="onboarding-complete" className="mt-10 border-neutral-200">
            <CardContent className="p-10 text-center">
              <Sparkles className="w-9 h-9 text-terracotta mx-auto" />
              <p className="text-neutral-600 mt-4 max-w-md mx-auto text-sm leading-relaxed">
                Your profile is live. Every approved hour you give counts towards
                badges, certificates and your community's story.
              </p>
              <Button
                data-testid="onboarding-complete-continue"
                onClick={() => nav(done.to)}
                className="mt-6 rounded-full bg-forest hover:bg-forest-hover px-8 h-11"
              >
                Continue
              </Button>
              <div className="flex items-center justify-center gap-x-6 gap-y-2 flex-wrap mt-6 text-sm">
                <Link to="/explore" data-testid="onboarding-explore-link" className="font-medium text-forest/70 hover:text-terracotta transition-colors">
                  Take the tour →
                </Link>
                <Link to="/pathways" data-testid="onboarding-pathways-link" className="font-medium text-forest/70 hover:text-terracotta transition-colors">
                  See the ways you can help →
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {!done && step === 1 && (
          <TypeStep type={type} setType={setType} onNext={() => setStep(2)} />
        )}

        {!done && step === 2 && (
          <Card className="mt-10 border-neutral-200">
            <CardContent className="p-8 space-y-6">
              <ProfileBasicsSection
                type={type}
                orgName={orgName} setOrgName={setOrgName}
                displayName={displayName} setDisplayName={setDisplayName}
                realName={realName} setRealName={setRealName}
                bio={bio} setBio={setBio}
                location={location} lat={lat}
                onLocationSelect={(r) => { setLocation(r.label); setLat(r.lat); setLng(r.lng); }}
                onLocationText={(v) => { setLocation(v); setLat(null); setLng(null); }}
                suggesting={suggesting}
                hasSuggestions={suggestions.length > 0}
                onSuggestSkills={suggestSkills}
              />
              <PrivacySection
                type={type}
                showRealName={showRealName} setShowRealName={setShowRealName}
                showOnLeaderboard={showOnLeaderboard} setShowOnLeaderboard={setShowOnLeaderboard}
                profilePrivate={profilePrivate} setProfilePrivate={setProfilePrivate}
                emailOptOut={emailOptOut} setEmailOptOut={setEmailOptOut}
              />
              <ValuesSection
                type={type}
                values={values}
                onToggle={(id) => toggle(values, setValues, id)}
              />
              <SkillsSection
                type={type}
                skills={skills}
                suggestions={suggestions}
                onToggle={(s) => toggle(skills, setSkills, s)}
                onAdd={addSuggestedSkill}
              />
              {user?.onboarded && (
                <div data-testid="settings-calendar-sync" className="pt-4 border-t border-neutral-100">
                  <label className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold inline-flex items-center gap-1.5">
                    <CalendarPlus className="w-3.5 h-3.5 text-terracotta" /> Calendar sync
                  </label>
                  <p className="text-xs text-neutral-500 mt-1.5">
                    Subscribe your calendar app to everything you sign up for — applications, RSVPs and events you host stay in sync automatically.
                  </p>
                  <CalendarSubscribeContent />
                </div>
              )}
              {user?.onboarded && <RoleBadgeSection />}
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(1)} className="rounded-full">Back</Button>
                <Button data-testid={VOLUNTEER.onboardingSubmit} onClick={submit} className="rounded-full bg-forest hover:bg-forest-hover px-6 h-11">
                  Save and continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
